import { useState } from "react";
import { PageMeta } from "@/components/PageMeta";
import { Link } from "wouter";
import {
  ChevronDown, Shield, CheckCircle2, Globe, Lock,
  FileText, Users, Tag, Headphones, Database, Eye,
} from "lucide-react";

const FAQS: { q: string; a: string }[] = [
  {
    q: "What is Autonomous GTM Infrastructure?",
    a: "Autonomous GTM Infrastructure is the software layer that runs your entire go-to-market motion — lead generation, outreach, SEO, campaigns, social, reputation, and pipeline — through self-directed AI modules instead of a stack of point tools operated by people. It doesn't just store data or wait for a trigger. It plans, executes, and optimizes growth work continuously across a shared data layer, while people supervise strategy and approve direction.",
  },
  {
    q: "How is Autonomous GTM Infrastructure different from a CRM or marketing automation?",
    a: "A CRM stores your customer data and waits for a human to act on it. Traditional marketing automation runs pre-built workflows that a person designs and triggers. Autonomous GTM Infrastructure acts on its own: it detects opportunities, launches work, measures results, and adjusts — rather than only following rules someone wired up in advance. It's a system of execution, not just a system of record.",
  },
  {
    q: "How do LaunchTactix's 70+ modules replace a traditional growth team?",
    a: "A traditional growth team splits work across specialists — an SEO lead, a content writer, an SDR team, a paid-media manager, a lifecycle marketer, a reputation manager, and a RevOps analyst. LaunchTactix maps each of those functions to autonomous modules that own the work end to end: the SEO module finds opportunities, drafts content, and ships it; outreach modules prospect, personalise, send, and handle replies; campaign modules build and optimise sequences; reputation modules monitor and respond. With all modules running on one shared data layer, you get the output of a full team without hiring, onboarding, or coordinating one.",
  },
  {
    q: "Does 'autonomous' mean there's no human in the loop?",
    a: "No. Autonomy applies to execution, not strategy. People set goals, define guardrails, and approve direction. But every automated action is observable and reviewable. The infrastructure removes the manual, repetitive execution that consumes a growth team's time — not human judgment. You stay in control of what ships.",
  },
  {
    q: "Which go-to-market functions do the modules cover?",
    a: "This module library spans the full funnel: lead generation and scoring, outreach and sequencing, reply handling, SEO and content, social media, paid and lifecycle campaigns, ABM, reputation management, and deal-pipeline orchestration. You can start with one module and add more as your growth motion scales — they coordinate automatically through the shared data layer.",
  },
  {
    q: "How quickly can I get started, and what does it cost?",
    a: "You can spin up your first autonomous modules for free — no growth team, agency, or long onboarding required. Add modules as you scale, and only pay as your usage grows. Most teams see modules producing work within their first session rather than after a multi-week implementation.",
  },
  {
    q: "Who is Autonomous GTM Infrastructure built for?",
    a: "It fits any business that needs the output of a full growth team without the headcount — founders, lean marketing teams, and agencies that want to scale SEO, outreach, campaigns, and pipelines without hiring a dedicated team for each function or stitching together a dozen point tools. Agencies in particular use it as a white-labeled service line.",
  },
  {
    q: "Can agencies white-label the platform under their own brand?",
    a: "Yes. Agencies can run the full platform under their own brand, logo, and domain, manage every client from one control plane with isolated workspaces, set their own pricing, and own the customer relationship. We handle the technology and model roadmap behind the scenes, so agencies can launch a high-margin recurring AI growth service without adding headcount.",
  },
  {
    q: "How does LaunchTactix handle security and compliance?",
    a: "Security is built into the platform architecture from the ground up. Data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Every workspace is isolated with a 7-tier role-based access control model — owner, admin, manager, sales_rep, member, billing_only, and viewer — enforced on every API endpoint. Authentication is handled by Clerk, an independently SOC 2 Type II certified identity provider, with MFA available at the workspace level. Every automated action is recorded in an append-only audit trail with actor identity, timestamp, IP, and workspace context. Security documentation is available on request for procurement evaluations.",
  },
  {
    q: "What compliance frameworks does the platform support?",
    a: "LaunchTactix ships with in-platform SOC 2 and ISO 27001 readiness dashboards — per-control evidence tracking aligned with the AICPA Trust Services Criteria and ISO/IEC 27001 Annex A, with live readiness scores and exportable CSV reports. These dashboards help your team review controls and evidence before procurement. The platform also includes built-in tooling for GDPR, CCPA, CASL, and CAN-SPAM compliance: unsubscribe management, suppression lists, bounce tracking, consent recording, and data retention policy enforcement. Asset, Vendor, and Risk Registers drive live control status across both readiness dashboards.",
  },
];

const AGENCY_FEATURES: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}[] = [
  {
    icon: Eye,
    title: "Your brand, front to back",
    body: "Run the full platform under your agency's name, logo, colors, and domain. Clients see your brand — not LaunchTactix — across dashboards, reports, and every module output.",
  },
  {
    icon: Users,
    title: "Multi-client workspaces",
    body: "Manage every client from one control plane. Spin up isolated workspaces, set per-account guardrails, and let the autonomous modules run growth work across your whole book of business.",
  },
  {
    icon: Tag,
    title: "Set your own pricing",
    body: "You own the customer relationship and the margin. Package the modules into your own tiers, bill clients directly, and add a high-margin recurring line without adding headcount.",
  },
  {
    icon: Headphones,
    title: "Partner-grade support",
    body: "We handle the engineering, infrastructure, and model roadmap behind the scenes. You get partner enablement and priority support so you can focus on winning and keeping accounts.",
  },
];

const SECURITY_POINTS = [
  "Data encrypted in transit (TLS 1.2+) and at rest (AES-256), with 7-tier role-based access controls on every workspace.",
  "Immutable audit trail — every automated action is recorded append-only with actor identity, timestamp, IP, and workspace context.",
  "Multi-tenant isolation — all data scoped by workspaceId; no cross-tenant leakage by design.",
  "In-platform SOC 2 and ISO 27001 readiness dashboards with live per-control evidence tracking.",
];

function Accordion({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/[0.08] rounded-xl bg-white/[0.02] hover:border-white/[0.14] transition-colors">
      <button
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <span className="text-white font-medium text-sm leading-snug">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-cyan-400 flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-6 pb-5 text-slate-400 text-sm leading-relaxed border-t border-white/[0.06] pt-4">
          {a}
        </div>
      )}
    </div>
  );
}

export function HelpCenter() {
  return (
    <div className="min-h-screen bg-[#030913] text-white">
      <PageMeta
        title="Help Center & FAQs — LaunchTactix"
        description="Get answers to common questions about LaunchTactix — autonomous AI modules, outreach, SEO, social media, and billing."
        canonical="https://launchtactix.com/help"
      />

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-950/30 via-transparent to-blue-950/20 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-3xl mx-auto px-6 pt-12 pb-16">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-xs font-mono tracking-widest uppercase text-cyan-400 border border-cyan-500/20 bg-cyan-500/8 rounded-full px-3 py-1.5 mb-8 hover:border-cyan-400/40 transition-colors"
          >
            ✦ HELP CENTER · FAQ
          </Link>

          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-5">
            Everything you need to know about Autonomous GTM Infrastructure.
          </h1>

          <p className="text-slate-400 text-lg mb-8 max-w-xl">
            How LaunchTactix's 72+ autonomous modules replace a traditional growth team, how agencies white-label the platform, and how we keep your data secure — answered.
          </p>

          <div className="flex flex-wrap gap-3 mb-10">
            <a
              href="/sign-up"
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm"
            >
              Start Free →
            </a>
            <a
              href="#faqs"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-slate-300 hover:text-white px-5 py-2.5 rounded-lg transition-colors text-sm"
            >
              Jump to questions
            </a>
          </div>

          <div className="flex flex-wrap gap-2">
            {["SOC 2 Aligned", "ISO 27001 Aligned", "GDPR Ready", "CCPA Ready", "CAN-SPAM Ready", "CASL Ready"].map(tag => (
              <span
                key={tag}
                className="text-xs font-mono tracking-wide border border-cyan-500/20 bg-cyan-500/5 text-cyan-400 rounded-full px-3 py-1"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pb-24 space-y-20">

        {/* ── What is section ─────────────────────────────────────────── */}
        <section>
          <p className="text-xs font-mono tracking-widest uppercase text-cyan-400 mb-4">
            THE SHORT ANSWER
          </p>
          <h2 className="text-3xl font-bold mb-6">What is Autonomous GTM Infrastructure?</h2>

          {/* Blue card */}
          <div className="rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-950/40 to-blue-950/30 p-6 mb-6 text-slate-100 text-[15px] leading-relaxed font-medium">
            Autonomous GTM Infrastructure is the software layer that runs your entire go-to-market motion — lead generation, outreach, SEO, campaigns, social, reputation, and pipeline — through self-directed AI modules instead of a stack of point tools operated by people. It plans, executes, and optimizes growth work continuously across a shared data layer, while people supervise strategy and approve direction.
          </div>

          <p className="text-slate-400 text-[15px] leading-relaxed mb-8">
            Unlike a CRM that stores data and waits, or marketing automation that only runs pre-built workflows, Autonomous GTM Infrastructure acts: it detects opportunities, launches work, measures results, and adjusts. LaunchTactix delivers this through autonomous modules — the output of a full growth team without the headcount.
          </p>

          {/* Feature tags */}
          <div className="flex flex-wrap gap-2">
            {["Full-funnel scope", "72+ autonomous modules", "Shared data layer", "Always-on execution", "Human oversight"].map(tag => (
              <span
                key={tag}
                className="text-xs font-medium border border-white/10 bg-white/[0.04] text-slate-300 rounded-lg px-3 py-1.5"
              >
                {tag}
              </span>
            ))}
          </div>
        </section>

        {/* ── FAQ ─────────────────────────────────────────────────────── */}
        <section id="faqs">
          <h2 className="text-3xl font-bold mb-2">Frequently asked questions</h2>
          <div className="h-px bg-gradient-to-r from-cyan-500/40 via-blue-500/20 to-transparent mb-8" />
          <div className="space-y-3">
            {FAQS.map(faq => (
              <Accordion key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </section>

        {/* ── FOR AGENCIES ─────────────────────────────────────────────── */}
        <section>
          <p className="text-xs font-mono tracking-widest uppercase text-cyan-400 mb-4">
            FOR AGENCIES
          </p>
          <h2 className="text-3xl font-bold mb-4">White-label the entire growth engine as your own.</h2>
          <p className="text-slate-400 text-[15px] leading-relaxed mb-10">
            Agencies use LaunchTactix to launch an AI growth service line in weeks, not quarters — reselling the 70+ autonomous modules under their own brand, pricing, and client relationships.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {AGENCY_FEATURES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="border border-white/[0.07] rounded-xl bg-white/[0.02] p-5 hover:border-cyan-500/30 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/15 flex items-center justify-center mb-4">
                  <Icon className="w-4 h-4 text-cyan-400" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-2">{title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── SECURITY & COMPLIANCE ────────────────────────────────────── */}
        <section>
          <p className="text-xs font-mono tracking-widest uppercase text-cyan-400 mb-4">
            SECURITY &amp; COMPLIANCE
          </p>
          <h2 className="text-3xl font-bold mb-4">Built for compliance readiness from day one.</h2>
          <p className="text-slate-400 text-[15px] leading-relaxed mb-8">
            Autonomy runs on trust. LaunchTactix ships with in-platform SOC 2 and ISO 27001 readiness dashboards — per-control evidence tracking, live readiness scores, and the compliance tooling your security team needs to evaluate the platform before procurement.
          </p>

          {/* SOC 2 */}
          <div className="border border-white/[0.08] rounded-2xl bg-white/[0.02] p-6 mb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/15 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <div className="text-white font-bold text-sm">SOC 2 Aligned</div>
                <div className="text-slate-500 text-xs font-mono tracking-wide">AICPA TRUST SERVICES CRITERIA · IN-PLATFORM READINESS DASHBOARD</div>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mt-3">
              The platform tracks alignment with the AICPA Trust Services Criteria across all five trust categories — Security, Availability, Confidentiality, Processing Integrity, and Privacy. Each control has per-control evidence recorded live from the platform's own data. The in-platform dashboard surfaces readiness scores and actionable remediation guidance for any gaps.
            </p>
          </div>

          {/* ISO 27001 */}
          <div className="border border-white/[0.08] rounded-2xl bg-white/[0.02] p-6 mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/15 flex items-center justify-center flex-shrink-0">
                <Globe className="w-4 h-4 text-cyan-400" />
              </div>
              <div>
                <div className="text-white font-bold text-sm">ISO 27001 Aligned</div>
                <div className="text-slate-500 text-xs font-mono tracking-wide">ISO/IEC 27001 ANNEX A · IN-PLATFORM READINESS DASHBOARD</div>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mt-3">
              The platform tracks alignment with ISO/IEC 27001 Annex A controls — covering information security policy, access control, cryptography, operations security, supplier relationships, and more. A live readiness dashboard gives your security team a control-by-control view with evidence and remediation guidance, exportable for procurement reviews.
            </p>
          </div>

          {/* Security points */}
          <div className="space-y-3">
            {SECURITY_POINTS.map(point => (
              <div
                key={point}
                className="flex items-start gap-3 border border-white/[0.07] rounded-xl bg-white/[0.02] px-5 py-4"
              >
                <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                <p className="text-slate-300 text-sm leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── CTA ─────────────────────────────────────────────────────── */}
        <section className="relative rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-950/30 to-blue-950/20 overflow-hidden p-10 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.08),transparent_70%)] pointer-events-none" />
          <div className="relative">
            <h2 className="text-3xl font-bold mb-4 leading-snug">
              Still have questions?<br />Put the modules to work.
            </h2>
            <p className="text-slate-400 mb-8 max-w-md mx-auto text-[15px] leading-relaxed">
              Spin up your first autonomous modules free and see how LaunchTactix runs your go-to-market — while you focus on the business.
            </p>
            <a
              href="/sign-up"
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-6 py-3 rounded-xl transition-colors text-sm"
            >
              Start Free →
            </a>
          </div>
        </section>

      </div>
    </div>
  );
}
